import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { AdminAccountRow } from '../lib/admin';
import { moneyFromDecimal } from '../lib/time';
import { tierLabel } from './BillingPlanCard';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill } from './StatusPill';

// The admin org list as a dense, selectable data table (spec §3.15). Presenter —
// the panel owns the accumulated rows + selection. The open org is the URL's
// (`?account=`), so the mono org name is a real LINK to it (`orgHref`), the one
// select control. A plain `<a link>`, not the router's `<A>`: `<A>` stamps
// `aria-current="page"` when its path minus the query is the current one, so on
// `/admin/orgs` every org link announced itself as the current page (the trap
// `AppNavLink` names). The router's delegated click handler still navigates it,
// and reads `replace` there, so picking through the list stays out of Back. The
// selected ROW carries `aria-current`. Shows tier + OSS pills, seats, spend-cap,
// members.
export function AdminOrgsTable(props: {
  rows: readonly AdminAccountRow[];
  selectedId: string | null;
  orgHref: (id: string) => string;
}): JSX.Element {
  const columns: readonly DataTableColumn<AdminAccountRow>[] = [
    {
      key: 'org',
      rowHeader: true,
      header: t('admin.col_org'),
      cell: (row) => (
        <>
          <a link replace class="data-table__mono" href={props.orgHref(row.id)}>
            {row.github_login}
          </a>
          <div class="admin-org-row__badges">
            <StatusPill>{tierLabel(row.tier)}</StatusPill>
            <Show when={row.oss_verified}>
              <StatusPill tone="success">{t('admin.org_oss_verified')}</StatusPill>
            </Show>
          </div>
        </>
      ),
    },
    {
      key: 'seats',
      header: t('admin.field_seats'),
      // #5914: "1 seats" is wrong; the count picks its plural form.
      cell: (row) => tPlural('admin.org_seats', row.seats, { seats: row.seats }),
    },
    {
      key: 'spend_cap',
      header: t('admin.field_spend_cap'),
      // Null is NO cap (metering reads it as a zero used-fraction), never `$0.00`,
      // which reads as a frozen account.
      cell: (row) =>
        row.spend_cap_usd === null
          ? t('admin.spend_cap_none')
          : moneyFromDecimal(row.spend_cap_usd),
    },
    {
      key: 'members',
      header: t('admin.members_heading'),
      // #5914: "1 members" is wrong; the count picks its plural form.
      cell: (row) => tPlural('admin.org_members', row.members_count, { count: row.members_count }),
    },
  ];

  return (
    <DataTable
      label={t('admin.orgs_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
      rowCurrent={(row) => props.selectedId === row.id}
    />
  );
}
